import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, Injector, input, output, signal } from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { NeoButtonToggleGroup, NeoButtonToggle } from '@neogov/pattern-library/neo-button-toggle';

export interface ButtonToggleOption {
  value: string;
  label: string;
  disabled?: boolean;
  ariaLabel?: string;
}

export type ButtonToggleSize = 'sm' | 'md';
export type ButtonToggleWidth = 'fill' | 'hug';

@Component({
  selector: 'cm-button-toggle',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NeoButtonToggleGroup, NeoButtonToggle],
  templateUrl: './button-toggle.component.html',
  styleUrl: './button-toggle.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: ButtonToggleComponent,
      multi: true,
    },
  ],
})
export class ButtonToggleComponent implements ControlValueAccessor {
  public readonly options = input.required<ButtonToggleOption[]>();
  public readonly ariaLabel = input.required<string>();
  public readonly autId = input.required<string>();
  public readonly multiple = input<boolean>(false);
  public readonly size = input<ButtonToggleSize>('md');
  public readonly buttonWidth = input<ButtonToggleWidth>('hug');
  public readonly vertical = input<boolean>(false);
  public readonly hideSingleSelectionIndicator = input<boolean>(true);
  public readonly hideMultipleSelectionIndicator = input<boolean>(true);

  public readonly valueChange = output<string | string[] | null>();

  protected readonly optionVms = computed(() =>
    this.options().map((option, i) => ({
      ...option,
      autId: `${this.autId()}-${option.value || i}`,
    }))
  );

  public value: string | string[] | null = null;
  public disabled = false;

  private readonly _injector = inject(Injector);
  public readonly ngControl = signal<NgControl | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onChange: (value: string | string[] | null) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onTouched: () => void = () => {};

  constructor() {
    // Defer NgControl lookup to avoid circular dependency
    queueMicrotask(() => {
      try {
        const ngControl = this._injector.get(NgControl, null, { optional: true, self: true }) || null;
        this.ngControl.set(ngControl);
      } catch {
        this.ngControl.set(null);
      }
    });
  }

  public writeValue(value: string | string[] | null): void {
    this.value = value;
  }

  public registerOnChange(fn: (value: string | string[] | null) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public onValueChange(newValue: string | string[] | null): void {
    this.value = newValue;
    this._onChange(newValue);
    this._onTouched();
    this.valueChange.emit(newValue);
  }

  public getButtonAutId(option: ButtonToggleOption, index: number): string {
    return `${this.autId()}-${option.value || index}`;
  }
}
